import type {
  CreateItemCategoryRequest,
  CreateItemRequest,
  CreateLegalEntityRequest,
  CreateManufacturerRequest,
  CreatePriceTierRequest,
  CreateUnitGroupRequest,
  CreateUnitRequest,
  ItemCategoryResponse,
  LegalEntityResponse,
  ItemResponse,
  ItemSortKey,
  ItemWriteResponse,
  ManufacturerResponse,
  PriceTierResponse,
  SortDir,
  UnitGroupResponse,
  UnitResponse,
  UpdateItemCategoryRequest,
  UpdateItemRequest,
  UpdateLegalEntityRequest,
  UpdateManufacturerRequest,
  UpdatePriceTierRequest,
  UpdateUnitGroupRequest,
  UpdateUnitRequest,
} from '@palaccount/contracts';
import { MAX_PICKER_IDS, PICKER_PAGE_SIZE, pickerIds } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * The item-master data layer: thin TanStack Query wrappers over the typed
 * contract client, the partners-feature pattern — every non-2xx becomes an
 * ApiError carrying the server's fixed sentence.
 */

export const UNITS_KEY = ['units'] as const;
export const TIERS_KEY = ['price-tiers'] as const;
export const CATEGORIES_KEY = ['item-categories'] as const;
export const ITEMS_KEY = ['items'] as const;

// ── units (MD-12) ───────────────────────────────────────────────────────────

export function useUnits(includeInactive = false) {
  return useQuery({
    queryKey: [...UNITS_KEY, { includeInactive }],
    queryFn: async (): Promise<UnitResponse[]> => {
      const response = await api.units.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.units;
    },
  });
}

export function useUnitMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: UNITS_KEY });
  const create = useMutation({
    mutationFn: async (body: CreateUnitRequest): Promise<UnitResponse> => {
      const response = await api.units.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ unitId, body }: { unitId: string; body: UpdateUnitRequest }) => {
      const response = await api.units.update({ params: { unitId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ unitId, active }: { unitId: string; active: boolean }) => {
      const response = active
        ? await api.units.reactivate({ params: { unitId }, body: {} })
        : await api.units.deactivate({ params: { unitId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const seedStarter = useMutation({
    mutationFn: async () => {
      const response = await api.units.seedStarter({ body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body.units;
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, seedStarter };
}

// ── unit groups (MD-12 field 7, OQ-05-18) ───────────────────────────────────

export const UNIT_GROUPS_KEY = ['unit-groups'] as const;

export function useUnitGroups(includeInactive = false) {
  return useQuery({
    queryKey: [...UNIT_GROUPS_KEY, { includeInactive }],
    queryFn: async (): Promise<UnitGroupResponse[]> => {
      const response = await api.unitGroups.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.groups;
    },
  });
}

export function useUnitGroupMutations() {
  const queryClient = useQueryClient();
  // Deleting or renaming a folder changes what units DISPLAY, so both keys.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: UNIT_GROUPS_KEY });
    void queryClient.invalidateQueries({ queryKey: UNITS_KEY });
  };
  const create = useMutation({
    mutationFn: async (body: CreateUnitGroupRequest): Promise<UnitGroupResponse> => {
      const response = await api.unitGroups.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ groupId, body }: { groupId: string; body: UpdateUnitGroupRequest }) => {
      const response = await api.unitGroups.update({ params: { groupId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ groupId, active }: { groupId: string; active: boolean }) => {
      const response = active
        ? await api.unitGroups.reactivate({ params: { groupId }, body: {} })
        : await api.unitGroups.deactivate({ params: { groupId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async ({ groupId }: { groupId: string }) => {
      const response = await api.unitGroups.remove({ params: { groupId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, remove };
}

// ── legal entities (MD-08 field 4's "Companies") ────────────────────────────

export const LEGAL_ENTITIES_KEY = ['legal-entities'] as const;

export function useLegalEntities(includeInactive = false) {
  return useQuery({
    queryKey: [...LEGAL_ENTITIES_KEY, { includeInactive }],
    queryFn: async (): Promise<LegalEntityResponse[]> => {
      const response = await api.legalEntities.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.entities;
    },
  });
}

export function useLegalEntityMutations() {
  const queryClient = useQueryClient();
  // Renaming an entity changes what warehouses DISPLAY, so both keys.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
    void queryClient.invalidateQueries({ queryKey: ['warehouses'] });
  };
  const create = useMutation({
    mutationFn: async (body: CreateLegalEntityRequest): Promise<LegalEntityResponse> => {
      const response = await api.legalEntities.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ entityId, body }: { entityId: string; body: UpdateLegalEntityRequest }) => {
      const response = await api.legalEntities.update({ params: { entityId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ entityId, active }: { entityId: string; active: boolean }) => {
      const response = active
        ? await api.legalEntities.reactivate({ params: { entityId }, body: {} })
        : await api.legalEntities.deactivate({ params: { entityId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async ({ entityId }: { entityId: string }) => {
      const response = await api.legalEntities.remove({ params: { entityId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, remove };
}

// ── manufacturers (IT-S6) ───────────────────────────────────────────────────

export const MANUFACTURERS_KEY = ['manufacturers'] as const;

export function useManufacturers(includeInactive = false) {
  return useQuery({
    queryKey: [...MANUFACTURERS_KEY, { includeInactive }],
    queryFn: async (): Promise<ManufacturerResponse[]> => {
      const response = await api.manufacturers.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.manufacturers;
    },
  });
}

export function useManufacturerMutations() {
  const queryClient = useQueryClient();
  // Renaming a maker changes what items DISPLAY, so both keys.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: MANUFACTURERS_KEY });
    void queryClient.invalidateQueries({ queryKey: ITEMS_KEY });
  };
  const create = useMutation({
    mutationFn: async (body: CreateManufacturerRequest): Promise<ManufacturerResponse> => {
      const response = await api.manufacturers.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({
      manufacturerId,
      body,
    }: {
      manufacturerId: string;
      body: UpdateManufacturerRequest;
    }) => {
      const response = await api.manufacturers.update({ params: { manufacturerId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ manufacturerId, active }: { manufacturerId: string; active: boolean }) => {
      const response = active
        ? await api.manufacturers.reactivate({ params: { manufacturerId }, body: {} })
        : await api.manufacturers.deactivate({ params: { manufacturerId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive };
}

// ── price tiers (MD-14, R5) ─────────────────────────────────────────────────

export function usePriceTiers(includeInactive = false, opts: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: opts.enabled ?? true,
    queryKey: [...TIERS_KEY, { includeInactive }],
    queryFn: async (): Promise<PriceTierResponse[]> => {
      const response = await api.priceTiers.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.tiers;
    },
  });
}

export function usePriceTierMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: TIERS_KEY });
  const create = useMutation({
    mutationFn: async (body: CreatePriceTierRequest): Promise<PriceTierResponse> => {
      const response = await api.priceTiers.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ tierId, body }: { tierId: string; body: UpdatePriceTierRequest }) => {
      const response = await api.priceTiers.update({ params: { tierId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ tierId, active }: { tierId: string; active: boolean }) => {
      const response = active
        ? await api.priceTiers.reactivate({ params: { tierId }, body: {} })
        : await api.priceTiers.deactivate({ params: { tierId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  /** MD-14 §6 "Move customers to…" — one audited action for the whole tier. */
  const moveCustomers = useMutation({
    mutationFn: async ({ tierId, targetTierId }: { tierId: string; targetTierId: string }) => {
      const response = await api.priceTiers.moveCustomers({
        params: { tierId },
        body: { targetTierId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  /**
   * BR-MD-14-08's endpoint half: the screen only offers Delete on a tier
   * nothing uses (§6), and the server's ERR-MD-14-04 refusal backstops it.
   */
  const remove = useMutation({
    mutationFn: async ({ tierId }: { tierId: string }) => {
      const response = await api.priceTiers.remove({ params: { tierId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, moveCustomers, remove };
}

// ── item categories (MD-04) ─────────────────────────────────────────────────

export function useItemCategories(includeInactive = false) {
  return useQuery({
    queryKey: [...CATEGORIES_KEY, { includeInactive }],
    queryFn: async (): Promise<ItemCategoryResponse[]> => {
      const response = await api.itemCategories.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.categories;
    },
  });
}

export function useItemCategoryMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
  const create = useMutation({
    mutationFn: async (body: CreateItemCategoryRequest): Promise<ItemCategoryResponse> => {
      const response = await api.itemCategories.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({
      categoryId,
      body,
    }: {
      categoryId: string;
      body: UpdateItemCategoryRequest;
    }) => {
      const response = await api.itemCategories.update({ params: { categoryId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ categoryId, active }: { categoryId: string; active: boolean }) => {
      const response = active
        ? await api.itemCategories.reactivate({ params: { categoryId }, body: {} })
        : await api.itemCategories.deactivate({ params: { categoryId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive };
}

// ── items (MD-03) ───────────────────────────────────────────────────────────

export interface ItemListFilters {
  q?: string | undefined;
  type?: 'product' | 'service' | 'bundle' | undefined;
  categoryId?: string | undefined;
  /** IT-S6: one maker's items. */
  manufacturerId?: string | undefined;
  /** `deleted` (IT-N4) is the only way a soft-deleted item is listed. */
  status?: 'active' | 'inactive' | 'deleted' | undefined;
  /** bugs: Items — Tracked / Untracked / All on "Track stock alerts". */
  alerts?: 'tracked' | 'untracked' | undefined;
  /** bugs: Items — a whitelisted sort column; absent = code order. */
  sort?: ItemSortKey | undefined;
  dir?: SortDir | undefined;
  /** BR-MD-03-07: what a stock document can actually move. */
  stockable?: boolean | undefined;
  /** BR-MD-03-05b: what a kit or a recipe may reference. */
  kitComponent?: boolean | undefined;
  /** Nothing is a part of itself. */
  excludeId?: string | undefined;
}

function itemQuery(filters: ItemListFilters) {
  return {
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.type ? { type: filters.type } : {}),
    ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
    ...(filters.manufacturerId ? { manufacturerId: filters.manufacturerId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.alerts ? { alerts: filters.alerts } : {}),
    ...(filters.sort ? { sort: filters.sort, dir: filters.dir ?? ('asc' as const) } : {}),
    ...(filters.stockable ? { stockable: 'true' as const } : {}),
    ...(filters.kitComponent ? { kitComponent: 'true' as const } : {}),
    ...(filters.excludeId ? { excludeId: filters.excludeId } : {}),
  };
}

/**
 * THE PICKER'S two reads (GEN-50), which replaced the one that asked for the
 * whole catalogue.
 *
 * Until this wave `useItems()` fetched every item — the unbounded opt-out,
 * 44.1 MB at PART 20.7's 50,000 — into a `<select>` on ~15 document editors,
 * and `UNPAGED_CALLERS` carried it as the countdown's biggest entry. Paging it was
 * refused three times, correctly: an item past the cut would have been
 * unpickable with nothing on screen to say so.
 *
 * A typeahead asks two bounded questions instead, and it needs BOTH:
 *
 *   useItemSearch(q)     — one panel of matches for what is being typed.
 *   useItemsHeld(ids)    — the items the document's saved lines already name,
 *                          resolved by id and by nothing else. This is the one
 *                          that makes it safe: without it, reopening an
 *                          invoice would render every line's item BLANK until
 *                          the reader typed its name back.
 */
export function useItemSearch(q: string, filters: ItemListFilters = {}) {
  return useQuery({
    queryKey: [...ITEMS_KEY, 'search', q, filters],
    queryFn: async (): Promise<ItemResponse[]> => {
      const response = await api.items.list({
        query: { ...itemQuery({ ...filters, q: q || undefined }), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.items;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The items a form is already holding. NOT filtered by status: an item
 * switched off after a document was written still has to render on that
 * document (BR-MD-01-07 is about what a picker OFFERS).
 */
export function useItemsHeld(ids: readonly (string | null | undefined)[]) {
  // Sorted and de-duplicated so that re-ordering lines does not look like a
  // different query to the cache; a free-text line names no item at all.
  const key = [...new Set(ids.filter((id): id is string => typeof id === 'string' && id !== ''))]
    .sort();
  return useQuery({
    queryKey: [...ITEMS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<ItemResponse[]> => {
      const response = await api.items.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.items;
    },
  });
}

/**
 * MD-03's catalogue SCREEN — one page at a time (NFR-42).
 *
 * `keepPreviousData` for the reason DSH-09 gives: pressing Next must update
 * the figures without the table blinking back to a skeleton and losing the
 * reader's place.
 */
export function usePagedItems(filters: ItemListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...ITEMS_KEY, 'page', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.items.list({
        query: { ...itemQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** MD-03 §4a's KPI strip: four numbers over the whole catalogue, from SQL. */
export function useItemStats() {
  return useQuery({
    queryKey: [...ITEMS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.items.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useItem(itemId: string | undefined) {
  return useQuery({
    queryKey: [...ITEMS_KEY, 'one', itemId],
    enabled: Boolean(itemId),
    queryFn: async (): Promise<ItemResponse> => {
      const response = await api.items.get({ params: { itemId: itemId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateItemRequest): Promise<ItemWriteResponse> => {
      const response = await api.items.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

export function useUpdateItem(itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateItemRequest): Promise<ItemWriteResponse> => {
      const response = await api.items.update({ params: { itemId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

/**
 * IT-N4 — the soft delete and its undo. The hard DELETE (MD-RULE-1's, for an
 * item used nowhere) is a different route and is not offered by the editor.
 */
export function useItemDelete(itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: 'delete' | 'restore'): Promise<ItemResponse> => {
      const response =
        action === 'delete'
          ? await api.items.softDelete({ params: { itemId }, body: {} })
          : await api.items.restore({ params: { itemId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

/** Switch off / switch on — MD-03 has no delete path in normal use (MD-RULE-1). */
export function useItemActive(itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: 'deactivate' | 'reactivate'): Promise<ItemResponse> => {
      const response =
        action === 'deactivate'
          ? await api.items.deactivate({ params: { itemId }, body: {} })
          : await api.items.reactivate({ params: { itemId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}
